import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 6: Movement Medicine
 * Abstract human figure using spheres at joints with connecting lines
 * Optimized: reusable Vector3 objects, no per-frame allocations
 */
export default function MovementFigure({ audioData, isActive, position = [0, 0, 0] }) {
  const groupRef = useRef()
  const jointRefs = useRef({})
  const lineRefs = useRef([])

  // Reusable Vector3s for per-frame line updates (avoids GC pressure)
  const _startPos = useMemo(() => new THREE.Vector3(), [])
  const _endPos = useMemo(() => new THREE.Vector3(), [])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Define joint positions (simplified humanoid)
  const joints = useMemo(() => ({
    head: { pos: [0, 1.5, 0], size: 0.2 },
    neck: { pos: [0, 1.2, 0], size: 0.1 },
    chest: { pos: [0, 0.8, 0], size: 0.15 },
    waist: { pos: [0, 0.3, 0], size: 0.12 },

    leftShoulder: { pos: [-0.4, 1.0, 0], size: 0.12 },
    leftElbow: { pos: [-0.7, 0.5, 0], size: 0.1 },
    leftHand: { pos: [-0.9, 0, 0], size: 0.08 },

    rightShoulder: { pos: [0.4, 1.0, 0], size: 0.12 },
    rightElbow: { pos: [0.7, 0.5, 0], size: 0.1 },
    rightHand: { pos: [0.9, 0, 0], size: 0.08 },

    leftHip: { pos: [-0.2, 0.1, 0], size: 0.1 },
    leftKnee: { pos: [-0.25, -0.5, 0], size: 0.1 },
    leftFoot: { pos: [-0.3, -1.0, 0], size: 0.08 },

    rightHip: { pos: [0.2, 0.1, 0], size: 0.1 },
    rightKnee: { pos: [0.25, -0.5, 0], size: 0.1 },
    rightFoot: { pos: [0.3, -1.0, 0], size: 0.08 }
  }), [])

  // Define connections between joints
  const connections = useMemo(() => [
    ['head', 'neck'],
    ['neck', 'chest'],
    ['chest', 'waist'],
    ['chest', 'leftShoulder'],
    ['leftShoulder', 'leftElbow'],
    ['leftElbow', 'leftHand'],
    ['chest', 'rightShoulder'],
    ['rightShoulder', 'rightElbow'],
    ['rightElbow', 'rightHand'],
    ['waist', 'leftHip'],
    ['leftHip', 'leftKnee'],
    ['leftKnee', 'leftFoot'],
    ['waist', 'rightHip'],
    ['rightHip', 'rightKnee'],
    ['rightKnee', 'rightFoot']
  ], [])

  // Cache joint name lookups for the animation loop
  const jointNames = useMemo(() => Object.keys(joints), [joints])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Gentle swaying of entire figure
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(time * 0.5) * 0.2
      groupRef.current.rotation.z = Math.cos(time * 0.3) * 0.1
      groupRef.current.position.y = position[1] + Math.sin(time * 0.7) * 0.1
    }

    // Pulse joints with beats
    for (let n = 0; n < jointNames.length; n++) {
      const name = jointNames[n]
      const joint = jointRefs.current[name]
      if (!joint) continue

      let pulseAmount = 0
      if (name.includes('head') || name.includes('Hand')) {
        pulseAmount = highs * 0.3
      } else if (name.includes('Elbow') || name.includes('Knee')) {
        pulseAmount = mids * 0.3
      } else {
        pulseAmount = bass * 0.3
      }

      if (beat > 0.5) pulseAmount += 0.2

      joint.scale.setScalar(1 + pulseAmount)

      if (name.includes('Hand')) {
        joint.position.y += Math.sin(time * 0.8 + Math.PI) * 0.001
      } else if (name.includes('Foot')) {
        joint.position.y += Math.sin(time * 0.6) * 0.001
      }

      if (joint.material) {
        joint.material.emissiveIntensity = 0.5 + amplitude * 1.5
      }
    }

    // Update line connections using reusable Vector3s
    for (let i = 0; i < connections.length; i++) {
      const line = lineRefs.current[i]
      if (!line) continue

      const [start, end] = connections[i]
      const startJoint = jointRefs.current[start]
      const endJoint = jointRefs.current[end]

      if (startJoint && endJoint) {
        startJoint.getWorldPosition(_startPos)
        endJoint.getWorldPosition(_endPos)

        const posArr = line.geometry.attributes.position.array
        posArr[0] = _startPos.x
        posArr[1] = _startPos.y
        posArr[2] = _startPos.z
        posArr[3] = _endPos.x
        posArr[4] = _endPos.y
        posArr[5] = _endPos.z
        line.geometry.attributes.position.needsUpdate = true
      }
    }
  })

  return (
    <group ref={groupRef} position={position}>
      {/* Joints */}
      {Object.entries(joints).map(([name, { pos, size }]) => (
        <mesh
          key={name}
          ref={(el) => (jointRefs.current[name] = el)}
          position={pos}
        >
          <sphereGeometry args={[size, 16, 16]} />
          <meshStandardMaterial
            color="#00CED1"
            emissive="#00CED1"
            emissiveIntensity={0.5}
            metalness={0.5}
            roughness={0.3}
          />
        </mesh>
      ))}

      {/* Connection lines */}
      {connections.map((connection, index) => {
        const [start, end] = connection
        const startPos = joints[start].pos
        const endPos = joints[end].pos

        return (
          <line
            key={`line-${index}`}
            ref={(el) => (lineRefs.current[index] = el)}
          >
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([...startPos, ...endPos])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color="#4DD0E1"
              transparent
              opacity={0.6}
              linewidth={2}
            />
          </line>
        )
      })}

      {/* Ambient glow around figure */}
      <pointLight position={[0, 0.5, 1]} intensity={1.5} color="#00CED1" distance={4} />
    </group>
  )
}
